import { scheduleConsultationFromSignup } from 'domains/consultations/freeConsultationSchedule';
import { ScheduleResult, scheduleMachine } from 'domains/signup/consultationSchedule/scheduleMachine';
import { ContactInfoResult, contactInfoMachine } from 'domains/signup/contactInfo/contactInfoMachine';
import { createSalesforceLead, mapSignupContextToLeadPayload } from 'domains/signup/data/leads';
import { DEFAULT_APPOINTMENT_TYPE, getLeadIDFromURL } from 'domains/signup/data/queryParams';
import { TherapyTarget } from 'domains/signup/data/types';
import { InsuranceResult, insuranceMachine } from 'domains/signup/insurance/insuranceMachine';
import { loadScreenerResults } from 'domains/signup/screener/data/localStorage';
import { ScreenerResults } from 'domains/signup/screener/data/results';
import { getRedirectAfterSignupComplete } from 'domains/signup/signupFlow/data/redirect';
import { isNull, isUndefined } from 'lodash';
import { MachineStateType } from 'utils/xstate/types';
import { assign, fromPromise, setup } from 'xstate';

// note(juan): this is part of the context and its expected to come from outside, set by whomever executes the machine
export type SignupMachineInput = {
  appointmentType: string;
  calendarID?: string;
  loadScreenerResults?: boolean;
};

const DEFAULT_CONFIG: SignupMachineInput = {
  appointmentType: DEFAULT_APPOINTMENT_TYPE,
  loadScreenerResults: false,
};

export type CompletedSignupMachineContext = {
  target: TherapyTarget;

  communicationConcerns: string;
  insuranceInfo: InsuranceResult;
  contactInfo: ContactInfoResult;

  leadID: string;

  scheduleInfo: ScheduleResult;
};

type InternalContext = Partial<CompletedSignupMachineContext> & {
  machineConfig: SignupMachineInput;
};

/** @xstate-layout N4IgpgJg5mDOIC5SwJZQHYFcAOBZAhgMYAWK6YAdADYD2+EZUAyoQE5hjmsBKcmVAF1gBiCDXIUyANxoBrSqgw4CJMpVr1GLdpzA8+g2Amk1C+ASnEBtAAwBdW3cShsNVBfHOQAD0QBmPwBWCj8ADkDQgHYARgAWAE54gDZIpPCAGhAAT0QAWniAigAmeNDo0Pjov3jUmyKAX3rMxSw8IlIJDQZ0ZjYOLl5YfiFRcUoTeQoW5Xa1ajpu3p0BgyFjdBkzD3RHK2inJBBXd0t0L18EXKCKWMiiorSbWNvQ28DAzJzLyMDoikSATZ4oEkkCbH5Gs00K0VB11AstH1dPohoZhI4vMcUNtznkiqEkhQnhU6kUbFU-EVop88gUihRArEyqECe8yj9ISBpm1VBJOAI9AAVfCsGACYRDABGAFtscLRWABBjDlicYcLoF4sVIn4bJFQk9ddF9fEaZcmYSktEiprYtEapFYlbApzubC5vy9IwAJLoIasfDoQhgUYSCaUViYdC+-2B4Pu8jKlxubGnXEIcoUaLk8pJIq625xIqRM1pUJZ6o1cEsvzRQIQppc6EzXmUT2sRgAYXEAiIAl9ADMaKHxhs5BGo930L3CP30EOE2Ak0cU2rQBdon8AXmSqlomlgWa-ElCfFblSKXU9e9Xc2eXCKO3u9LpVGUFtTlPg6w-RLMDLsWfV90HfcxP3Eb8-WXVU03VWl-hsE8gViEFSipG0zVyCoKHCUJdQCRIkirWJbyUe8PWnPQgLfD9xC-PRf0lIhZGg1dYPXPJUizfUijtI0KjPD5sjyJ0-BwgkbBzA191CUiYVmCRYBISB+C7cRUV7bYR0kMdJjdBSFGUiBVJ6KcNLA8R1k2CydnsViTk8OCEDE4F8WLFI81BFlqWEjMrUJZ59ziMprTtJI5JbB8lOIFSqDUv1hhs4Q9FYGhWAobAqHMIdWGlKY70XKYjJMqAzMS7YrNMGzdnsTE2McjiEBKWIbhKfFj3xSJUiEr5gpsBlYnzGICl+aoIvIvlKI7HoWBi4yqBDMQw10id0Fm2KwEXezUwanxEHtBk83KIJwWPcEkjNKoDpSQaDVuckogbKEyMKvowJ6AAZMB6G08N8pegyKDeixPu+iBKto2yHFqlV6rOJyEhuQiT2tSpwgiWJLsksTyT1PNKyIplxte9h3qgL6fpStKMqygQcry-TWyB0mQfJsGIequyYeTBz4caoJIhuUEUl+E9HQCPwzVefqjWeGxwitUJ7kaRt0BoCA4C8Rm4Tq3n0yuPwWuJeJSXJAIqUw7N8yJHU6zCNJN2LYnAa6RFlj0QZhngWG9acwX61iOpwjJJlgQyXz8muLraySW4EjiG9G21iiBVYeUxV1na+b2hAUizQOHmNdqyStTDAi43jwUd-zKRdJOCsB9sfQSgMgzATO1xz3IrQZao7ViSkfiiEEesQUWbeQzcahLuvnvkpmm9Mns+0HGgO-Yrue-rM84kHiIfiSUeMypYoHk1EoTc1AJwvrgGF6m6iQMh+if29nms-1+kLxKIjUKV0KLoR0pISaoNgQQJDJD8GIzsmbRVivFcyncYK7QuPkBk7xJKxwNKUG6oRLooWCIyIaPwgS8QKDAh8C16CdhZqcAAoqwVKrB14oLHiybUTJDboUpGEKWYQcLkgHsaJ4aQkhPSbHfKKd5nyZUVO3H2H8nKbiJHWXiXUWQD1uEES6PwtwJCiDqMIgdKgUJTl6GaxUFosOzqgre1RqiBBtHackvFLp2woCkPUuoQSvFZKYiQwNGAUwgNY9M1xEjlCISIoKktfJKy1FEY6wIxEpACP4yghB1LlXoYwtKoSnIRC1Pvc2ySHhEXwZELUTxiGgiIXhFW9QgA */
export const signupMachine = setup({
  types: {
    context: {} as InternalContext,
    events: {} as
      | { type: 'back' }
      | { type: 'submitTarget'; target: TherapyTarget }
      | { type: 'enterRunScreener'; runScreener: boolean }
      | { type: 'submitCommunicationConcerns'; communicationConcerns: string },

    input: {} as SignupMachineInput,
  },

  actions: {
    saveScreenerResults: assign((_, params: { data: ScreenerResults }) => ({
      target: (params.data as ScreenerResults).targetPatient,
      communicationConcerns: (params.data as ScreenerResults).communicationConcerns,
    })),
    saveTarget: assign((_, params: { target: TherapyTarget }) => ({
      target: params.target,
    })),

    saveCommunicationConcerns: assign((_, params: { communicationConcerns: string }) => ({
      communicationConcerns: params.communicationConcerns,
    })),

    saveContactInfo: assign((_, params: { contactInfo: ContactInfoResult }) => ({
      contactInfo: params.contactInfo,
    })),

    saveInsuranceInfo: assign((_, params: { insuranceInfo: InsuranceResult }) => ({
      insuranceInfo: params.insuranceInfo,
    })),

    saveScheduleInfo: assign((_, params: { scheduleInfo: ScheduleResult }) => ({
      scheduleInfo: params.scheduleInfo,
    })),

    saveLeadID: assign((_, params: { leadID: string }) => ({
      leadID: params.leadID,
    })),

    saveLeadIDFromURL: assign(() => ({
      leadID: getLeadIDFromURL() as string,
      target: 'adult' as TherapyTarget, // Assume adult
      insuranceInfo: {
        useInsurance: false,
      } as InsuranceResult,
    })),

    redirectOnComplete: (_, params: { context: CompletedSignupMachineContext }) => {
      window.location.replace(getRedirectAfterSignupComplete(params.context));
    },
  },
  actors: {
    createLead: fromPromise(({ input }: { input: CompletedSignupMachineContext }) =>
      createSalesforceLead(mapSignupContextToLeadPayload(input)),
    ),

    runInsuranceMachine: insuranceMachine,
    runContactInfoMachine: contactInfoMachine,
    runScheduleMachine: scheduleMachine,
    loadScreenerResults: fromPromise(({ input }: { input: { loadScreenerResults?: boolean } }) => {
      return new Promise<ScreenerResults | null>(resolve => {
        if (!input.loadScreenerResults) {
          resolve(null);
        }
        resolve(loadScreenerResults());
      });
    }),
    scheduleConsultation: fromPromise(({ input }: { input: InternalContext }) => {
      return scheduleConsultationFromSignup(input as CompletedSignupMachineContext, {
        appointmentType: input.machineConfig.appointmentType,
        calendarID: input.machineConfig.calendarID,
      });
    }),
  },
  guards: {
    loadedScreenerResultsCorrectly: (_, params: { data: ScreenerResults | null }) => !isNull(params.data),
    hasLeadID: () => !isUndefined(getLeadIDFromURL()),
  },
}).createMachine({
  id: 'signupMachine',

  context: ({ input }) => ({
    machineConfig: {
      ...DEFAULT_CONFIG,
      ...input,
    },
  }),
  initial: 'loadingScreenerResults',
  states: {
    loadingScreenerResults: {
      invoke: {
        src: 'loadScreenerResults',
        input: ({
          context: {
            machineConfig: { loadScreenerResults },
          },
        }) => ({ loadScreenerResults }),

        onDone: [
          {
            target: 'enteringContactInfo',

            guard: {
              type: 'loadedScreenerResultsCorrectly',
              params: ({ event }) => ({ data: event.output }),
            },

            actions: {
              type: 'saveScreenerResults',
              params: ({ event: { output } }) => ({ data: output as ScreenerResults }),
            },
          },
          'enterTarget',
        ],
      },

      always: {
        target: 'enteringSchedule',
        guard: 'hasLeadID',
        actions: ['saveLeadIDFromURL'],
      },
    },

    enterTarget: {
      on: {
        submitTarget: {
          target: 'enterCommunicationConcerns',
          actions: {
            type: 'saveTarget',
            params: ({ event }) => event,
          },
        },
      },
    },

    enteringInsurance: {
      invoke: {
        id: 'runInsuranceMachine',
        src: 'runInsuranceMachine',

        input: ({ context }) => ({
          state: context.contactInfo?.state as string,
        }),

        onDone: {
          target: 'creatingLead',
          actions: {
            type: 'saveInsuranceInfo',
            params: ({ event }) => ({ insuranceInfo: event.output }),
          },
        },
      },
    },

    enteringContactInfo: {
      invoke: {
        id: 'runContactInfoMachine',
        src: 'runContactInfoMachine',

        input: ({ context }) => ({
          target: context.target,
          // only offer live phone calls if running the direct signup flow (no screener)
          offerLivePhoneCall: !context.machineConfig.loadScreenerResults,
        }),

        onDone: {
          target: 'enteringInsurance',
          actions: {
            type: 'saveContactInfo',
            params: ({ event }) => ({ contactInfo: event.output }),
          },
        },
      },
    },

    enterCommunicationConcerns: {
      on: {
        submitCommunicationConcerns: {
          target: 'enteringContactInfo',
          actions: {
            type: 'saveCommunicationConcerns',
            params: ({ event }) => event,
          },
        },

        back: 'enterTarget',
      },
    },

    schedulingConsultation: {
      invoke: {
        src: 'scheduleConsultation',
        input: ({ context }) => context,
        onDone: 'signupComplete',
        onError: 'consultationError',
      },
    },

    leadCreationError: {},

    signupComplete: {
      entry: {
        type: 'redirectOnComplete',
        params: ({ context }) => ({ context: context as CompletedSignupMachineContext }),
      },
    },

    enteringSchedule: {
      invoke: {
        id: 'runScheduleMachine',
        src: 'runScheduleMachine',

        input: ({ context }) => ({
          machineConfig: {
            appointmentType: context.machineConfig.appointmentType as string,
            calendarID: context.machineConfig.calendarID,
          },
        }),

        onDone: {
          target: 'schedulingConsultation',
          actions: {
            type: 'saveScheduleInfo',
            params: ({ event }) => ({ scheduleInfo: event.output }),
          },
        },
      },
    },

    creatingLead: {
      invoke: {
        src: 'createLead',

        input: ({ context }) => context as CompletedSignupMachineContext,
        onDone: {
          target: 'enteringSchedule',

          actions: {
            type: 'saveLeadID',
            params: ({ event }) => ({ leadID: event.output }),
          },
        },
        onError: {
          target: 'leadCreationError',
        },
      },
    },

    consultationError: {},
  },
});

export type SignupState = MachineStateType<typeof signupMachine>;
